import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PerfilBadge, StatusUsuarioBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import { formatarDataHora } from "@/lib/format";
import type { PagedResult, Perfil, StatusUsuario, UsuarioListado } from "@/lib/types";

export function UsuariosPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState<"all" | Perfil>("all");
  const [filtroStatus, setFiltroStatus] = useState<"all" | StatusUsuario>("all");
  const [editando, setEditando] = useState<UsuarioListado | null>(null);
  const [novoPerfil, setNovoPerfil] = useState<Perfil>("user");
  const [novoStatus, setNovoStatus] = useState<StatusUsuario>("active");

  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (busca.trim()) params.set("q", busca.trim());
  if (filtroPerfil !== "all") params.set("perfil", filtroPerfil);
  if (filtroStatus !== "all") params.set("status", filtroStatus);

  const { data, isLoading } = useQuery({
    queryKey: ["usuarios", page, busca, filtroPerfil, filtroStatus],
    queryFn: () => apiFetch<PagedResult<UsuarioListado>>(`/api/admin/usuarios?${params}`),
  });

  const atualizar = useMutation({
    mutationFn: (input: { id: string; perfil: Perfil; ativo: boolean }) =>
      apiFetch(`/api/admin/usuarios/${input.id}`, {
        method: "PATCH",
        body: { perfil: input.perfil, ativo: input.ativo },
      }),
    onSuccess: () => {
      toast.success("Usuário atualizado");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      setEditando(null);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Falha ao atualizar"),
  });

  function abrirEdicao(u: UsuarioListado) {
    setEditando(u);
    setNovoPerfil(u.perfil);
    setNovoStatus(u.status);
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold">Usuários</h1>
        <p className="text-sm text-muted-foreground">Gerenciar perfis, status e bloqueios.</p>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="busca" className="mb-1.5 block text-xs">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="busca"
              placeholder="E-mail ou nome..."
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
        </div>
        <div className="w-40">
          <Label className="mb-1.5 block text-xs">Perfil</Label>
          <Select
            value={filtroPerfil}
            onValueChange={(v) => {
              setFiltroPerfil(v as typeof filtroPerfil);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="user">Usuário</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="mb-1.5 block text-xs">Status</Label>
          <Select
            value={filtroStatus}
            onValueChange={(v) => {
              setFiltroStatus(v as typeof filtroStatus);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Base de usuários</CardTitle>
          <CardDescription>{data ? `${data.total} usuário(s)` : "Carregando..."}</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-mail</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data && data.items.length > 0 ? (
                data.items.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell>{u.nome || "—"}</TableCell>
                    <TableCell>
                      <PerfilBadge perfil={u.perfil} />
                    </TableCell>
                    <TableCell>
                      <StatusUsuarioBadge status={u.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatarDataHora(u.criadoEm)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => abrirEdicao(u)}>
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Nenhum usuário encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-muted-foreground">
            Página {data.page} de {data.totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={!data.hasPrevious} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="size-4" />
            Anterior
          </Button>
          <Button variant="outline" size="sm" disabled={!data.hasNext} onClick={() => setPage((p) => p + 1)}>
            Próxima
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}

      <Dialog open={editando !== null} onOpenChange={(o) => !o && setEditando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
            <DialogDescription>{editando?.email}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="edit-perfil">Perfil</Label>
              <Select value={novoPerfil} onValueChange={(v) => setNovoPerfil(v as Perfil)}>
                <SelectTrigger id="edit-perfil">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuário</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={novoStatus} onValueChange={(v) => setNovoStatus(v as StatusUsuario)}>
                <SelectTrigger id="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              onClick={() =>
                editando &&
                atualizar.mutate({ id: editando.id, perfil: novoPerfil, ativo: novoStatus === "active" })
              }
              disabled={atualizar.isPending}
            >
              {atualizar.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
